import React from 'react';
import styled, { css } from 'styled-components';

import ArrowDownIcon from '@/assets/svg/arrow_down32px.svg';
import { useTypedSelector } from '@/store/store';
import Link from '@/components/Link';
import { selectSinglePost } from '@/store/reducers/single-post';
import { colors } from '@/constants/theme';
import { getPhoneLink } from '@/utils/common';
import { media } from '@/utils/mixin';
import useCollapse from '@/hooks/useCollapse';

function DealerCenters() {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
  const page = useTypedSelector(selectSinglePost);

  if (!page || !page.dealers || page.dealers.length === 0) {
    return null;
  }

  return (
    <Component>
      <Display {...getToggleProps()} type="button">
        <DisplayText>Дилерские центры</DisplayText>
        <DisplayIcon isExpanded={isExpanded}>
          <ArrowDownIcon />
        </DisplayIcon>
      </Display>
      <section {...getCollapseProps()}>
        <Content>
          <DealerList>
            {page.dealers.map((dealer, index) => (
              <DealerItem key={index}>
                <Dealer>
                  <DealerLabel>
                    <DealerLabelLink to={dealer.url}>
                      {dealer.name}
                    </DealerLabelLink>
                  </DealerLabel>
                  <DealerPhones>
                    {dealer.phones &&
                      dealer.phones.length > 0 &&
                      dealer.phones.map((phone, index) => (
                        <DealerTel key={index} href={getPhoneLink(phone)}>
                          {phone}
                        </DealerTel>
                      ))}
                  </DealerPhones>
                  <DealerHolonym
                    dangerouslySetInnerHTML={{
                      __html: dealer.legalName ?? '',
                    }}
                  />
                </Dealer>
              </DealerItem>
            ))}
          </DealerList>
        </Content>
      </section>
    </Component>
  );
}

export default DealerCenters;

const Component = styled.div`
  border-top: 1px solid #d0d0d0;
  border-bottom: 1px solid #d0d0d0;
`;

const Display = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px 0;

  @media (min-width: 1400px) {
    padding: 40px 0;
  }
`;

const DisplayText = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 140%;
  color: ${(props) => props.theme.black100};

  @media (min-width: 1400px) {
    font-size: 25px;
  }
`;

const DisplayIcon = styled.span<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.main};
  transition: transform 0.2s ease-in-out;

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;

    @media (min-width: 1400px) {
      width: 32px;
      height: 32px;
    }
  }

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      transform: rotate(180deg);
    `};
`;

const Content = styled.div`
  padding-top: 10px;
  padding-bottom: 20px;

  @media (min-width: 1400px) {
    padding-top: 4px;
    padding-bottom: 40px;
  }
`;

const DealerList = styled.ul``;

const DealerItem = styled.li`
  &:not(:last-child) {
    margin-bottom: 20px;

    @media (min-width: 1400px) {
      margin-bottom: 48px;
    }
  }
`;

const Dealer = styled.div`
  ${media.mobileUp(
    css`
      display: grid;
      grid-template-columns: 1fr max-content 1fr;
      gap: 20px;
    `
  )};

  @media (min-width: 992px) {
    gap: 50px;
  }

  @media (min-width: 1400px) {
    gap: 100px;
  }
`;

const DealerLabel = styled.div`
  margin-bottom: 10px;

  font-size: 16px;
  line-height: 140%;
  color: ${colors.main};

  ${media.mobileUp(
    css`
      margin: 0;
    `
  )};

  @media (min-width: 1400px) {
    font-size: 20px;
    line-height: 140%;

    color: ${colors.main};
  }
`;

const DealerLabelLink = styled(Link)`
  color: ${colors.main};
  transition: 0.3s all ease;

  &:hover {
    opacity: 0.8;
  }
`;

const DealerPhones = styled.div`
  display: flex;
  flex-direction: column;
`;

const DealerTel = styled.a`
  margin-top: 10px;
  margin-bottom: 10px;

  font-weight: 300;
  font-size: 16px;
  line-height: 140%;
  color: ${(props) => props.theme.black100};
  transition: 0.3s all ease;

  &:hover {
    opacity: 0.8;
  }

  ${media.mobileUp(
    css`
      margin: 0;
      text-align: right;
    `
  )};

  @media (min-width: 1400px) {
    font-weight: 300;
    font-size: 18px;
    line-height: 140%;
  }
`;

const DealerHolonym = styled.div`
  margin-top: 10px;
  font-weight: 300;
  font-size: 16px;
  line-height: 140%;
  color: ${(props) => props.theme.black100};

  ${media.mobileUp(
    css`
      margin: 0;
      text-align: right;
    `
  )};

  @media (min-width: 1400px) {
    font-weight: 300;
    font-size: 18px;
    line-height: 140%;
  }
`;
